import React, { useState } from 'react';
import { Calculator, ArrowRight } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import { calculateVAT } from '../../../../../utils/calculations/finance';

/**
 * Компонент боковой панели для быстрого расчета НДС
 */
const QuickCalculationSidebar = ({ 
  formData, 
  setFormData, 
  errors, 
  isCalculating,
  vatRates
}) => {
  // Состояние для быстрого расчета
  const [quickAmount, setQuickAmount] = useState('1000');
  const [quickTaxRate, setQuickTaxRate] = useState('20');
  const [quickResult, setQuickResult] = useState(null);
  
  // Обработчик изменения суммы
  const handleAmountChange = (e) => {
    setQuickAmount(e.target.value);
  };
  
  // Обработчик изменения ставки НДС
  const handleTaxRateChange = (e) => {
    setQuickTaxRate(e.target.value);
  };
  
  // Функция быстрого расчета
  const performQuickCalculation = () => {
    // Парсим входные данные
    const amount = parseFloat(quickAmount.replace(',', '.')) || 0;
    let taxRate = quickTaxRate;
    
    if (amount <= 0) {
      return;
    }
    
    // Выполняем расчеты для двух случаев
    const includesVat = false; // Для начисления НДС
    
    // Определяем числовую ставку НДС для расчета
    let numTaxRate;
    if (taxRate === 'exempt') {
      numTaxRate = 0;
    } else if (taxRate === '20/120') {
      numTaxRate = 20;
    } else if (taxRate === '10/110') {
      numTaxRate = 10;
    } else {
      numTaxRate = parseFloat(taxRate);
    }
    
    // Выполняем расчеты
    const addResult = calculateVAT(amount, numTaxRate, includesVat);
    const extractResult = calculateVAT(amount, numTaxRate, true);
    
    setQuickResult({
      taxRate: taxRate,
      add: {
        amountWithoutVAT: amount,
        vatAmount: addResult.vatAmount,
        amountWithVAT: addResult.amountWithVAT
      },
      extract: {
        amountWithVAT: amount,
        vatAmount: extractResult.vatAmount,
        amountWithoutVAT: extractResult.amountWithoutVAT
      }
    });
  };
  
  // Применение расчета к основной форме
  const applyToMainForm = (type) => {
    if (!quickResult) return;
    
    // Установка значений в основную форму
    setFormData(prev => ({
      ...prev,
      calculationType: type,
      amount: type === 'add' 
        ? quickAmount 
        : quickResult.extract.amountWithVAT.toString(),
      taxRate: quickTaxRate
    }));
  };
  
  // Предопределенные ставки НДС для быстрого выбора
  const commonRates = ['20', '10', '0', 'exempt'];
  const extractRates = ['20/120', '10/110'];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый расчет НДС
      </h3>
      
      <div className="space-y-4">
        {/* Сумма */}
        <div>
          <label htmlFor="quickAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Сумма
          </label>
          <div className="relative">
            <input
              type="text"
              id="quickAmount"
              value={quickAmount}
              onChange={handleAmountChange}
              className="form-input w-full pl-7"
              placeholder="Введите сумму"
            />
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <span className="text-gray-500 dark:text-gray-400">₽</span>
            </div>
          </div>
        </div>
        
        {/* Ставка НДС */}
        <div>
          <label htmlFor="quickTaxRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ставка НДС
          </label>
          <select
            id="quickTaxRate"
            value={quickTaxRate}
            onChange={handleTaxRateChange}
            className="form-select w-full"
          >
            {commonRates.map(rate => (
              <option key={rate} value={rate}>
                {rate === 'exempt' ? 'Без НДС' : `${rate}%`}
              </option>
            ))}
            {extractRates.map(rate => (
              <option key={rate} value={rate}>
                {rate} (расчетная)
              </option>
            ))}
          </select>
        </div>
        
        {/* Кнопки быстрого выбора ставок */}
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Выбрать ставку
          </label>
          <div className="grid grid-cols-4 gap-1">
            {commonRates.map(rate => (
              <button
                key={rate}
                type="button"
                onClick={() => setQuickTaxRate(rate)}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${quickTaxRate === rate 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rate === 'exempt' ? 'Без НДС' : `${rate}%`}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={performQuickCalculation}
          icon={<Calculator className="w-4 h-4" />}
        >
          Быстрый расчет
        </Button>
        
        {/* Результаты быстрого расчета */}
        {quickResult && (
          <div className="mt-2 space-y-4">
            {/* Начисление НДС */}
            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3 border border-blue-100 dark:border-blue-800">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                Начисление НДС {quickResult.taxRate === 'exempt' ? '(без НДС)' : ''}
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Сумма без НДС:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(quickResult.add.amountWithoutVAT)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">НДС:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {quickResult.taxRate === 'exempt' 
                      ? 'Без НДС' 
                      : formatCurrency(quickResult.add.vatAmount)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-blue-200 dark:border-blue-700 pt-1 mt-1">
                  <span className="font-medium text-blue-800 dark:text-blue-300">Итого с НДС:</span>
                  <span className="font-medium text-blue-800 dark:text-blue-300">
                    {formatCurrency(quickResult.add.amountWithVAT)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => applyToMainForm('add')}
                className="w-full mt-2 text-xs flex items-center justify-center gap-1 text-blue-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Применить к форме <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            
            {/* Выделение НДС */}
            <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-3 border border-green-100 dark:border-green-800">
              <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                Выделение НДС {quickResult.taxRate === 'exempt' ? '(без НДС)' : ''}
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Сумма с НДС:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(quickResult.extract.amountWithVAT)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">НДС:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {quickResult.taxRate === 'exempt' 
                      ? 'Без НДС' 
                      : formatCurrency(quickResult.extract.vatAmount)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-green-200 dark:border-green-700 pt-1 mt-1">
                  <span className="font-medium text-green-800 dark:text-green-300">Сумма без НДС:</span>
                  <span className="font-medium text-green-800 dark:text-green-300">
                    {formatCurrency(quickResult.extract.amountWithoutVAT)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => applyToMainForm('extract')}
                className="w-full mt-2 text-xs flex items-center justify-center gap-1 text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
              >
                Применить к форме <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickCalculationSidebar;